/**
 * Phát hiện môi trường trình duyệt (in-app browser Facebook/Messenger/Instagram/Zalo) và
 * chuỗi fallback tải ảnh — theo docs/73-facebook-integration.md §2 và docs/71-consumer-ux.md §10.
 *
 * ⚠️ QUAN TRỌNG: hành vi WebView của Facebook/Messenger/Instagram/Zalo KHÔNG được Meta (hay Zalo)
 * tài liệu hoá chính thức và đổi theo phiên bản app. Mọi nhánh dưới đây là phòng thủ
 * (feature-detect trước, suy đoán theo tên app sau) — các chỗ đánh dấu "CẦN TEST THIẾT BỊ THẬT"
 * là giả định chưa được xác minh trên thiết bị thật, theo đúng tinh thần
 * docs/73-facebook-integration.md §2.5 (ma trận kiểm thử bắt buộc, không tự động hoá được).
 */

export type InAppApp = "facebook" | "messenger" | "instagram" | "zalo" | null;
export type OsKind = "ios" | "android" | "other";

export type InAppBrowserInfo = {
  isInApp: boolean;
  app: InAppApp;
  os: OsKind;
};

function detectOs(ua: string): OsKind {
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "other";
}

/**
 * Nhận diện in-app browser qua User-Agent.
 *
 * Token dùng (không phân biệt hoa/thường), theo docs/73-facebook-integration.md §2.1:
 * - "instagram" → Instagram app
 * - "fban" / "fbav" / "fb_iab" / "fbsv" → Facebook/Messenger app (không phân biệt được tin cậy
 *   hai app này chỉ bằng UA — CẦN TEST THIẾT BỊ THẬT nếu cần tách riêng Messenger)
 * - "zalo" → Zalo app — ⚠️ CẦN XÁC MINH: token UA thật của Zalo chưa được tài liệu hoá công khai,
 *   "zalo" là suy đoán hợp lý (nhiều app Việt Nam gắn tên mình vào UA) nhưng CHƯA xác nhận trên
 *   thiết bị thật (docs/73-facebook-integration.md §2.1 đánh dấu mục này CẦN XÁC MINH).
 */
export function detectInAppBrowser(userAgent: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): InAppBrowserInfo {
  const ua = userAgent.toLowerCase();
  const os = detectOs(ua);

  if (ua.includes("instagram")) return { isInApp: true, app: "instagram", os };
  if (ua.includes("zalo")) return { isInApp: true, app: "zalo", os };

  const hasFacebookToken = ua.includes("fban") || ua.includes("fbav") || ua.includes("fb_iab") || ua.includes("fbsv");
  if (hasFacebookToken) {
    // Không có cách phân biệt tin cậy Facebook app vs Messenger app chỉ từ UA — mặc định "facebook".
    return { isInApp: true, app: "facebook", os };
  }

  // WebView Android chung chung không gắn nhãn nguồn cụ thể (không phải Chrome độc lập).
  if (os === "android" && /\bwv\b/.test(ua) && !ua.includes("chrome/")) {
    return { isInApp: true, app: null, os };
  }

  return { isInApp: false, app: null, os };
}

/**
 * Ước lượng khả năng tải file trực tiếp (thẻ `<a download>`) có đáng tin hay không.
 * Không phải feature-detect API thật (trình duyệt không có API nào báo trước "download sẽ
 * thành công") — đây là suy đoán bảo thủ dựa trên docs/73-facebook-integration.md §2.3 bảng
 * phương án #2: coi `<a download>` là KHÔNG đáng tin trong mọi in-app browser đã biết, đáng tin
 * ở trình duyệt độc lập. CẦN TEST THIẾT BỊ THẬT theo ma trận §2.5 trước khi coi là đúng tuyệt đối.
 */
export function canDownloadFile(info: InAppBrowserInfo = detectInAppBrowser()): boolean {
  if (info.isInApp) return false;
  if (typeof document === "undefined") return false;
  return "download" in document.createElement("a");
}

export type DownloadMethod = "share" | "direct-download" | "open-fullsize-guide";

export type DownloadResult =
  | { method: "share" }
  | { method: "direct-download" }
  | { method: "open-fullsize-guide"; imageUrl: string };

/**
 * Chuỗi fallback tải ảnh — theo đúng thứ tự docs/73-facebook-integration.md §2.3:
 *   1. navigator.share({ files }) nếu hỗ trợ share file thật (feature-detect bằng canShare).
 *   2. <a download> qua Blob URL — CHỈ thử khi không phải in-app browser đã biết là rủi ro
 *      (đúng nguyên tắc "Bước 3 nên là mặc định an toàn khi không chắc chắn về bước 1/2").
 *   3. Trả về "open-fullsize-guide" — component gọi hàm này chịu trách nhiệm hiển thị ảnh
 *      toàn màn hình + hướng dẫn "Chạm giữ vào ảnh rồi chọn Lưu ảnh" (không tự động được,
 *      hành vi long-press-save là cấp hệ điều hành, không phụ thuộc JS).
 *
 * Không bao giờ tự coi bước 2 là "đã tải xong" mà không xác nhận — caller phải tự hiển thị
 * thông báo rõ ràng cho người dùng (docs/71-consumer-ux.md §10.2).
 */
export async function downloadImageWithFallback(
  blob: Blob,
  filename: string,
  info: InAppBrowserInfo = detectInAppBrowser(),
): Promise<DownloadResult> {
  const file = new File([blob], filename, { type: blob.type });

  if (typeof navigator !== "undefined" && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "QR Studio" });
      return { method: "share" };
    } catch {
      // Người dùng huỷ share sheet, hoặc share thất bại — rơi xuống phương án kế tiếp.
    }
  }

  if (canDownloadFile(info)) {
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return { method: "direct-download" };
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }
  }

  return { method: "open-fullsize-guide", imageUrl: URL.createObjectURL(blob) };
}

/**
 * Android — mở URL hiện tại bằng Chrome qua Intent URL (docs/73-facebook-integration.md §2.4).
 * ⚠️ CẦN TEST THIẾT BỊ THẬT: hành vi khi không cài Chrome, và liệu WebView Facebook/Zalo có chặn
 * scheme `intent://` hay không — chưa xác minh, chỉ dùng làm nút "lối thoát", không phải đường
 * chính, nên thất bại im lặng (không throw) là chấp nhận được.
 */
export function buildAndroidChromeIntentUrl(targetUrl: string): string {
  const withoutScheme = targetUrl.replace(/^https?:\/\//, "");
  return `intent://${withoutScheme}#Intent;scheme=https;package=com.android.chrome;end`;
}

/**
 * iOS không có scheme URL chính thức nào để "mở trang hiện tại bằng Safari" từ WebView của app
 * khác (docs/73-facebook-integration.md §2.4) — chỉ có thể hướng dẫn thao tác thủ công.
 */
export function iosOpenInBrowserInstructions(): string {
  return 'Chạm nút "⋯" (góc trên hoặc dưới màn hình, tuỳ app) → chọn "Mở bằng trình duyệt khác" hoặc "Open in Safari".';
}
